import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { fetchProductFamiliesApi } from "@/api/checkoutApi";
// import useCheckoutMap from "@/hooks/useCheckoutMap";
import useCheckoutPageProps from "@/hooks/useCheckoutPageProps";
import { CheckoutContext } from "@/providers/context/CheckoutContext";
import { ProductFamilyInterface } from "@/ts/interfaces/Checkout/ProductFamilyInterface";
import { ProductFamilyApiInterface } from "@/ts/interfaces/Checkout/api/ProductFamilyApiInterface";
import { mapProductFamiliesApiData } from "@/utils/checkoutDataMapper";
import { sortItems } from "@/utils/utils";

import { useToast } from "@chakra-ui/react";

export const useCheckoutContext = () => {
    const { i18n } = useTranslation();

    const toast = useToast();

    const { productFamilyUrlsByCountry } = useCheckoutPageProps();
    const checkoutContext = useContext(CheckoutContext);

    const [productFamilies, setProductFamilies] = useState<
        ProductFamilyApiInterface[] | null
    >(null);

    if (!checkoutContext) {
        throw new Error(
            "checkoutContext must be used within an CheckoutContextProvider"
        );
    }

    useEffect(() => {
        // Get translated product families on language switch
        if (productFamilies) {
            let convertedProductFamilies: ProductFamilyInterface[] = [];
            convertedProductFamilies = mapProductFamiliesApiData(
                sortItems<ProductFamilyApiInterface>(productFamilies, "order", [
                    "addons",
                    "plans"
                ])
            );
            onUpdateProductFamilies(convertedProductFamilies);
        }
    }, [i18n.language]);

    const { onUpdateProductFamilies, onUpdateIsProductFamiliesLoading } =
        checkoutContext;

    const fetchProductFamilies = async (country: string, lang: string) => {
        const updatedCountry = country === "international" ? "AU" : country;
        const productFamilyUrl =
            Object.entries(productFamilyUrlsByCountry).find(
                ([countryCode]) => countryCode === updatedCountry
            )?.[1] ?? "";

        const productFamilyUrlBasedOnLang = productFamilyUrl.replace(
            /\.json$/,
            lang !== "en" ? `_${lang}.json` : ".json"
        );

        try {
            onUpdateIsProductFamiliesLoading(true);
            const response = await fetchProductFamiliesApi(
                productFamilyUrlBasedOnLang
            );

            setProductFamilies(response?.data ?? null);
            let convertedProductFamilies: ProductFamilyInterface[] = [];
            convertedProductFamilies = mapProductFamiliesApiData(
                sortItems<ProductFamilyApiInterface>(
                    response?.data ?? [],
                    "order",
                    ["addons", "plans"]
                )
            );
            onUpdateProductFamilies(convertedProductFamilies);
        } catch (error) {
            onUpdateProductFamilies([]);
            console.error(
                "Could not load products for the selected region",
                error
            );
            toast({
                description: "Could not load products for the selected region.",
                status: "error",
                containerStyle: {
                    marginBottom: 10
                }
            });
        } finally {
            onUpdateIsProductFamiliesLoading(false);
        }
    };

    return { fetchProductFamilies };
};
